import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';

import { DIALOG_DATA, DialogRef, ButtonModule } from '@hglobal/common';
import { WorkContextOption } from '@fb/models';
import { TranslateInPipe } from '@fb/pipes/translate-in.pipe';

import { groupWorkContexts, workContextKey, workContextLabel } from '../../helpers/work-context';

/** Heading keys of the groups this build knows; an unknown group is headed by its code. */
const WORK_CONTEXT_GROUP_TITLE_KEYS: Readonly<Record<string, string>> = {
  OPERATIONAL: 'chatBot.workContextPicker.group.operational',
  SUPPORT: 'chatBot.workContextPicker.group.support',
};

export type AiChatWorkContextPickerDialogData = {
  /** `WORK.workContexts` in response order. */
  options: WorkContextOption[];
  /** Primary action: send pending vs start new draft. */
  confirmSendMode: boolean;
};

@Component({
  selector: 'pu-ai-chat-work-context-picker-dialog',
  imports: [ButtonModule, TranslateInPipe],
  templateUrl: './ai-chat-work-context-picker-dialog.component.html',
  styleUrl: './ai-chat-work-context-picker-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TranslateInPipe],
})
export class AiChatWorkContextPickerDialogComponent {
  readonly #dialogRef = inject(DialogRef);
  readonly #data = inject<AiChatWorkContextPickerDialogData>(DIALOG_DATA);

  readonly confirmSendMode = this.#data.confirmSendMode;

  protected readonly groups = groupWorkContexts(this.#data.options).map(group => ({
    ...group,
    titleKey: WORK_CONTEXT_GROUP_TITLE_KEYS[group.group] ?? null,
  }));
  protected readonly workContextKey = workContextKey;
  protected readonly workContextLabel = workContextLabel;

  protected readonly selected = signal<WorkContextOption | null>(this.#data.options[0] ?? null);

  /** Closes the dialog with the whole picked option. */
  confirm(): void {
    const option = this.selected();

    if (option) {
      this.#dialogRef.close(option);
    }
  }

  /** Closes the dialog without a pick. */
  cancel(): void {
    this.#dialogRef.close(undefined);
  }

  /** Whether the option is the currently highlighted one. */
  protected isSelected(option: WorkContextOption): boolean {
    const selected = this.selected();

    return selected !== null && workContextKey(selected) === workContextKey(option);
  }

  /** Highlights the option. */
  protected pick(option: WorkContextOption): void {
    this.selected.set(option);
  }
}
